import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from './api';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from '../hooks/use-toast';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { Plus, Search, Trash2, Edit, Phone, Mail, Filter, Users } from 'lucide-react';
import { useOwnerMode } from './OwnerModeContext';
import { useAuth } from './AuthContext';
import { OwnerChip, OwnerAvatar, OwnerFilter, OwnerSelect, useStaff, ownerParam, groupByOwner } from './kit/OwnerChip';
import PhoneInput from './shared/PhoneInput';
import MoneyInput from './shared/MoneyInput';
import NumberInput from './shared/NumberInput';
import AddressFields from './shared/AddressFields';
import { emptyAddress, addressPayload, addressLine } from './shared/address';
import { LOAN_TYPE_LABELS, labelFor, optionsFrom } from './shared/labels';
import { formatPhone } from './shared/phone';
import useOpenNewFromUrl from './kit/useOpenNewFromUrl';

// ─────────────────────────────────────────────────────────────
// Mortgage-side (LEADS) config
// ─────────────────────────────────────────────────────────────
const MORTGAGE_STAGE_COLORS = {
  new: '#94a3b8', contacted: '#3b82f6', qualified: '#8b5cf6', application: '#f59e0b',
  processing: '#f97316', approved: '#8DC63F', closed_won: '#22c55e', closed_lost: '#ef4444',
};
const MORTGAGE_STAGE_LABELS = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified', application: 'Application',
  processing: 'Processing', approved: 'Approved', closed_won: 'Closed Won', closed_lost: 'Closed Lost',
};
const EMPTY_LEAD = {
  name: '', email: '', phone: '', loan_type: 'purchase', loan_amount: '',
  property_address: '', property_address_parts: emptyAddress(), status: 'new', source: 'Manual', credit_score: '', notes: '',
};

// ─────────────────────────────────────────────────────────────
// Real-estate side (RE CONTACTS as leads) config
// ─────────────────────────────────────────────────────────────
const RE_TYPE_COLORS = {
  buyer: '#3b82f6', seller: '#8b5cf6', past_client: '#22c55e', sphere: '#a855f7',
  referral_partner: '#f59e0b', investor: '#ec4899', renter: '#06b6d4', lead: '#94a3b8', other: '#64748b',
};
const RE_TYPE_LABELS = {
  buyer: 'Buyer', seller: 'Seller', past_client: 'Past Client', sphere: 'Sphere',
  referral_partner: 'Referral Partner', investor: 'Investor', renter: 'Renter', lead: 'Lead', other: 'Other',
};
const EMPTY_RE = {
  first_name: '', last_name: '', email: '', phone: '', contact_type: 'buyer',
  price_range_min: '', price_range_max: '', preferred_areas: '', source: 'Manual',
  tags: '', notes: '',
};

const fmt$ = (n) => n ? Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '—';

export default function LeadsPage() {
  const { effectiveProductType } = useOwnerMode();
  const isRE = effectiveProductType === 'real_estate';
  // Ticket 116: owner column / filter / reassign (mortgage leads only).
  const { user } = useAuth();
  const me = user?.user_id;
  const canViewAll = (user?.permissions || []).includes('loans.view_all');
  const staff = useStaff();
  const [ownerFilter, setOwnerFilter] = useState(canViewAll ? 'all' : 'mine');
  const [groupOwners, setGroupOwners] = useState(false);
  const [bulkOwner, setBulkOwner] = useState('');

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [filterVal, setFilterVal] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(isRE ? EMPTY_RE : EMPTY_LEAD);
  // Bulk selection state — reset on mode switch or refetch
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStage, setBulkStage] = useState('');
  const navigate = useNavigate();

  const rowKey = (r) => isRE ? r.contact_id : r.lead_id;
  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === rows.length && rows.length > 0) setSelectedIds(new Set());
    else setSelectedIds(new Set(rows.map(rowKey)));
  };
  const clearSelection = () => { setSelectedIds(new Set()); setBulkOwner(''); };

  const bulkApplyStage = async () => {
    if ((!bulkStage && !bulkOwner) || selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    try {
      let res;
      if (isRE) {
        if (!bulkStage) return;
        res = await api.post('/re/contacts/bulk-update', { contact_ids: ids, patch: { contact_type: bulkStage } });
      } else {
        const patch = { ...(bulkStage ? { status: bulkStage } : {}), ...(bulkOwner ? { owner_id: bulkOwner } : {}) };
        res = await api.post('/leads/bulk-update', { lead_ids: ids, patch });
      }
      const matched = res?.data?.matched ?? 0;
      const reassigned = res?.data?.reassigned ?? 0;
      if (matched === 0) {
        toast({ title: 'Nothing updated', description: 'No matching rows found (permissions or stale IDs).' });
      } else {
        toast({ title: `Updated ${matched} row${matched !== 1 ? 's' : ''}`, description: reassigned ? `${reassigned} reassigned` : undefined });
      }
      clearSelection(); setBulkStage(''); setBulkOwner(''); load();
    } catch (e) { toast({ title: 'Bulk update failed', description: e.response?.data?.detail || 'Error' }); }
  };
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!(await confirmDialog({ title: `Delete ${selectedIds.size} row${selectedIds.size !== 1 ? 's' : ''}?`, description: 'This cannot be undone.', confirmText: 'Delete', destructive: true }))) return;
    const ids = Array.from(selectedIds);
    try {
      const endpoint = isRE ? '/re/contacts/bulk-delete' : '/leads/bulk-delete';
      const key = isRE ? 'contact_ids' : 'lead_ids';
      await api.post(endpoint, { [key]: ids });
      toast({ title: `Deleted ${ids.length} row${ids.length !== 1 ? 's' : ''}` });
      clearSelection(); load();
    } catch (e) { toast({ title: 'Bulk delete failed', description: e.response?.data?.detail || 'Error' }); }
  };

  // ──────────────── LOAD ────────────────
  const load = async () => {
    if (isRE) {
      const params = filterVal ? { contact_type: filterVal } : {};
      const { data } = await api.get('/re/contacts', { params });
      setRows(data);
    } else {
      const params = { ...(filterVal ? { status: filterVal } : {}), ...ownerParam(ownerFilter, me) };
      const { data } = await api.get('/leads', { params });
      setRows(data);
    }
  };

  // Ticket 116: hand a lead to another teammate (avatar click / dialog).
  const reassign = async (row, ownerId) => {
    try {
      const { data } = await api.post(`/leads/${row.lead_id}/owner`, { owner_id: ownerId });
      const moved = data?.auto_tasks_moved || 0;
      toast({ title: `Reassigned to ${data?.owner_name || 'teammate'}`, description: moved ? `${moved} open task${moved === 1 ? '' : 's'} moved along` : undefined });
      load();
    } catch (e) { toast({ title: 'Reassign failed', description: e.response?.data?.detail?.message || e.response?.data?.detail || 'Error' }); }
  };
  // Reload on mode switch or filter change; also reset the form template.
  // Clear rows FIRST so the tbody doesn't briefly render stale objects
  // (RE contacts have no `name`) through the wrong-mode row template.
  useEffect(() => {
    setForm(isRE ? EMPTY_RE : EMPTY_LEAD);
    setFilterVal('');
    setOwnerFilter(canViewAll ? 'all' : 'mine');
    setRows([]);
    setSelectedIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRE]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [effectiveProductType, filterVal, ownerFilter]);

  // ──────────────── CRUD ────────────────
  const openNew = () => { setEditing(null); setForm(isRE ? EMPTY_RE : { ...EMPTY_LEAD, owner_id: me }); setOpen(true); };
  useOpenNewFromUrl(openNew); // the top-bar + menu links here with ?new=1
  const openEdit = (r) => {
    setEditing(r);
    if (isRE) {
      setForm({
        ...EMPTY_RE,
        ...r,
        price_range_min: r.price_range_min || '',
        price_range_max: r.price_range_max || '',
        preferred_areas: (r.preferred_areas || []).join(', '),
        tags: (r.tags || []).join(', '),
      });
    } else {
      setForm({
        ...EMPTY_LEAD, ...r, loan_amount: r.loan_amount || '', credit_score: r.credit_score || '',
        property_address_parts: r.property_address_parts
          ? { ...emptyAddress(), ...r.property_address_parts }
          : { ...emptyAddress(), street: r.property_address || '' },
      });
    }
    setOpen(true);
  };

  const save = async () => {
    try {
      if (isRE) {
        const payload = {
          ...form,
          price_range_min: form.price_range_min === '' ? null : form.price_range_min,
          price_range_max: form.price_range_max === '' ? null : form.price_range_max,
          preferred_areas: form.preferred_areas ? form.preferred_areas.split(',').map(s => s.trim()).filter(Boolean) : [],
          tags: form.tags ? form.tags.split(',').map(s => s.trim()).filter(Boolean) : [],
        };
        if (editing) { await api.patch(`/re/contacts/${editing.contact_id}`, payload); toast({ title: 'Contact updated' }); }
        else         { await api.post('/re/contacts', payload); toast({ title: 'Contact created' }); }
      } else {
        const { owner_id, ...rest } = form;
        const parts = addressPayload(form.property_address_parts);
        const payload = {
          ...rest,
          loan_amount: form.loan_amount === '' ? null : form.loan_amount,
          credit_score: form.credit_score === '' ? null : form.credit_score,
          property_address_parts: parts,
          property_address: addressLine(parts),
        };
        if (editing) {
          // PATCH ignores owner_id by design; reassignment is its own call.
          await api.patch(`/leads/${editing.lead_id}`, payload);
          if (owner_id && owner_id !== editing.owner_id) await api.post(`/leads/${editing.lead_id}/owner`, { owner_id });
          toast({ title: 'Lead updated' });
        } else {
          await api.post('/leads', { ...payload, ...(owner_id ? { owner_id } : {}) });
          toast({ title: 'Lead created' });
        }
      }
      setOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail || 'Failed' }); }
  };

  const del = async (row) => {
    if (!(await confirmDialog({ title: isRE ? 'Delete this contact?' : 'Delete this lead?', confirmText: 'Delete', destructive: true }))) return;
    try {
      if (isRE) await api.delete(`/re/contacts/${row.contact_id}`);
      else      await api.delete(`/leads/${row.lead_id}`);
      toast({ title: 'Deleted' });
      load();
    } catch (e) { toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Error' }); }
  };

  // ──────────────── DERIVED ────────────────
  const filtered = rows.filter((r) => {
    if (!q) return true;
    if (isRE) return `${r.first_name || ''} ${r.last_name || ''} ${r.email || ''} ${r.phone || ''}`.toLowerCase().includes(q.toLowerCase());
    return ((r.name || '') + (r.email || '') + (r.phone || '')).toLowerCase().includes(q.toLowerCase());
  });

  // Mortgage row (shared by the flat and the by-owner views).
  const renderMortgageRow = (r) => (
    <tr key={r.lead_id} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/portal/leads/${r.lead_id}`)} data-testid={`lead-row-${r.lead_id}`}>
      <td className="pl-6 pr-2 py-4" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectedIds.has(r.lead_id)} onChange={() => toggleOne(r.lead_id)} data-testid={`bulk-row-${r.lead_id}`} className="accent-green-600" /></td>
      <td className="px-4 py-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-semibold text-xs" style={{ background: '#070B5F', color: '#A6E56C' }}>{r.name?.[0]?.toUpperCase() || '?'}</div>
          <div>
            <div className="whitespace-nowrap font-semibold text-sm" style={{ color: '#070B5F' }}>{r.name}</div>
            <div className="whitespace-nowrap text-xs text-slate-500">{r.source} · Score {r.score || 50}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-4 text-sm text-slate-600">
        {r.email && <div className="flex items-center gap-1.5"><Mail size={12} />{r.email}</div>}
        {r.phone && <div className="flex items-center gap-1.5 mt-0.5"><Phone size={12} />{formatPhone(r.phone)}</div>}
      </td>
      <td className="px-4 py-4 text-sm" style={{ color: '#070B5F' }}>{labelFor(LOAN_TYPE_LABELS, r.loan_type)}</td>
      <td className="px-4 py-4 font-semibold text-sm" style={{ color: '#070B5F' }}>{fmt$(r.loan_amount)}</td>
      <td className="px-4 py-4">
        <span className="text-xs px-2 py-1 rounded-full font-semibold uppercase tracking-wider" style={{ background: (MORTGAGE_STAGE_COLORS[r.status] || '#94a3b8') + '20', color: MORTGAGE_STAGE_COLORS[r.status] || '#94a3b8' }}>
          {MORTGAGE_STAGE_LABELS[r.status]}
        </span>
      </td>
      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
        <OwnerChip userId={r.owner_id} name={r.owner_name} staff={staff} onChange={(uid) => reassign(r, uid)} showName={!groupOwners} testId={`lead-owner-${r.lead_id}`} />
      </td>
      <td className="px-4 py-4 text-right" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => openEdit(r)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`lead-edit-${r.lead_id}`}><Edit size={14} style={{ color: '#070B5F' }} /></button>
        <button onClick={() => del(r)} className="p-2 hover:bg-slate-100 rounded-lg ml-1" data-testid={`lead-delete-${r.lead_id}`}><Trash2 size={14} className="text-red-500" /></button>
      </td>
    </tr>
  );

  // ──────────────── RENDER ────────────────
  const pageTitle = isRE ? 'Real Estate Leads' : 'Leads';
  const pageSubtitle = isRE ? 'Manage every buyer, seller & sphere prospect in one place.' : 'Manage every prospect in one place.';
  const newLabel = isRE ? 'New Contact' : 'New Lead';
  const searchPlaceholder = isRE ? 'Search contacts by name, email, phone…' : 'Search leads by name, email, phone…';
  const filterAllLabel = isRE ? 'All types' : 'All statuses';
  const filterOptions = isRE ? Object.entries(RE_TYPE_LABELS) : Object.entries(MORTGAGE_STAGE_LABELS);
  const emptyMsg = rows.length === 0
    ? (isRE ? 'No contacts yet. Click "New Contact" to add your first.' : 'No leads yet. Click "New Lead" to add your first.')
    : (isRE ? 'No contacts match your search.' : 'No leads match your search.');

  return (
    <div className="p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="font-serif text-3xl font-bold" style={{ color: '#070B5F' }} data-testid="leads-title">{pageTitle}</h1>
          <p className="text-slate-500 mt-1" data-testid="leads-subtitle">{pageSubtitle}</p>
        </div>
        <button onClick={openNew} className="btn-gold" data-testid="leads-new-btn"><Plus size={16} /> {newLabel}</button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder} className="pl-10" data-testid="leads-search" />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={15} className="text-slate-400 shrink-0" />
            <Select value={filterVal || 'all'} onValueChange={(v) => setFilterVal(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-full md:w-48" data-testid="leads-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{filterAllLabel}</SelectItem>
                {filterOptions.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {!isRE && (
            <div className="flex flex-wrap items-center gap-2">
              <OwnerFilter value={ownerFilter} onChange={setOwnerFilter} staff={staff} canViewAll={canViewAll} testId="leads-owner-filter" />
              {canViewAll && (
                <button
                  type="button"
                  onClick={() => setGroupOwners((v) => !v)}
                  aria-pressed={groupOwners}
                  data-testid="leads-group-owners"
                  title="Group leads by owner"
                  className={`h-10 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 transition ${groupOwners ? 'text-white border-transparent' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  style={groupOwners ? { background: '#070B5F' } : {}}
                >
                  <Users size={14} /> By owner
                </button>
              )}
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          {/* Scrolls sideways on phones instead of crushing the columns. */}
          <table className="w-full min-w-[860px] md:min-w-0">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="pl-6 pr-2 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={rows.length > 0 && selectedIds.size === rows.length}
                    onChange={toggleAll}
                    data-testid="bulk-select-all"
                    className="accent-green-600"
                  />
                </th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">{isRE ? 'Type' : 'Loan'}</th>
                <th className="px-4 py-3">{isRE ? 'Price Range' : 'Amount'}</th>
                <th className="px-4 py-3">{isRE ? 'Score' : 'Status'}</th>
                {!isRE && <th className="px-4 py-3">Owner</th>}
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={isRE ? 7 : 8} className="text-center py-16 text-slate-400">{emptyMsg}</td></tr>
              ) : (!isRE && groupOwners ? groupByOwner(filtered, staff).flatMap((g) => [
                <tr key={`grp-${g.owner_id}`} className="bg-slate-50/70" data-testid={`owner-group-${g.owner_id || 'none'}`}>
                  <td colSpan={8} className="pl-6 pr-4 py-2">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                      <OwnerAvatar userId={g.owner_id} name={g.name} size="sm" />
                      <span style={{ color: '#070B5F' }}>{g.name}</span>
                      <span className="text-slate-400 normal-case tracking-normal font-medium">· {g.rows.length} lead{g.rows.length === 1 ? '' : 's'}</span>
                    </div>
                  </td>
                </tr>,
                ...g.rows.map(renderMortgageRow),
              ]) : filtered).map((r) => {
                if (React.isValidElement(r)) return r;
                if (isRE) {
                  const fullName = `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Unnamed';
                  const initial = (r.first_name?.[0] || r.last_name?.[0] || '?').toUpperCase();
                  const range = (r.price_range_min || r.price_range_max)
                    ? `${fmt$(r.price_range_min)} – ${fmt$(r.price_range_max)}`
                    : '—';
                  const color = RE_TYPE_COLORS[r.contact_type] || '#94a3b8';
                  return (
                    <tr key={r.contact_id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`lead-row-${r.contact_id}`}>
                      <td className="pl-6 pr-2 py-4"><input type="checkbox" checked={selectedIds.has(r.contact_id)} onChange={() => toggleOne(r.contact_id)} data-testid={`bulk-row-${r.contact_id}`} className="accent-green-600" /></td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center font-semibold text-xs" style={{ background: '#070B5F', color: '#A6E56C' }}>{initial}</div>
                          <div>
                            <div className="whitespace-nowrap font-semibold text-sm" style={{ color: '#070B5F' }}>{fullName}</div>
                            <div className="whitespace-nowrap text-xs text-slate-500">{r.source || 'Manual'} · Score {r.lead_score || 50}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-600">
                        {r.email && <div className="flex items-center gap-1.5"><Mail size={12} />{r.email}</div>}
                        {r.phone && <div className="flex items-center gap-1.5 mt-0.5"><Phone size={12} />{formatPhone(r.phone)}</div>}
                      </td>
                      <td className="px-4 py-4">
                        <span className="text-xs px-2 py-1 rounded-full font-semibold uppercase tracking-wider" style={{ background: color + '20', color }}>
                          {RE_TYPE_LABELS[r.contact_type] || r.contact_type}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold" style={{ color: '#070B5F' }}>{range}</td>
                      <td className="px-4 py-4 text-sm font-semibold" style={{ color: '#6BA82E' }}>{r.lead_score || 50}</td>
                      <td className="px-4 py-4 text-right">
                        <button onClick={() => openEdit(r)} className="p-2 hover:bg-slate-100 rounded-lg" data-testid={`lead-edit-${r.contact_id}`}><Edit size={14} style={{ color: '#070B5F' }} /></button>
                        <button onClick={() => del(r)} className="p-2 hover:bg-slate-100 rounded-lg ml-1" data-testid={`lead-delete-${r.contact_id}`}><Trash2 size={14} className="text-red-500" /></button>
                      </td>
                    </tr>
                  );
                }
                return renderMortgageRow(r);
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="lead-dialog">
          <DialogHeader>
            <DialogTitle style={{ color: '#070B5F' }} className="font-serif">
              {editing ? (isRE ? 'Edit Contact' : 'Edit Lead') : (isRE ? 'New Contact' : 'New Lead')}
            </DialogTitle>
          </DialogHeader>
          {isRE ? (
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>First Name *</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1" data-testid="re-first" /></div>
              <div><Label>Last Name</Label><Input value={form.last_name || ''} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Phone</Label><PhoneInput value={form.phone} onChange={(d) => setForm({ ...form, phone: d })} className="mt-1" /></div>
              <div><Label>Contact Type</Label>
                <Select value={form.contact_type} onValueChange={(v) => setForm({ ...form, contact_type: v })}>
                  <SelectTrigger className="mt-1" data-testid="re-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(RE_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Source</Label><Input value={form.source || ''} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1" placeholder="Zillow, referral, sphere…" /></div>
              <div><Label>Min Price</Label><MoneyInput value={form.price_range_min} onChange={(n) => setForm({ ...form, price_range_min: n })} className="mt-1" data-testid="re-min" /></div>
              <div><Label>Max Price</Label><MoneyInput value={form.price_range_max} onChange={(n) => setForm({ ...form, price_range_max: n })} className="mt-1" data-testid="re-max" /></div>
              <div className="md:col-span-2"><Label>Preferred Areas (comma-separated)</Label><Input value={form.preferred_areas || ''} onChange={(e) => setForm({ ...form, preferred_areas: e.target.value })} className="mt-1" placeholder="Austin, Round Rock, Cedar Park" /></div>
              <div className="md:col-span-2"><Label>Tags (comma-separated)</Label><Input value={form.tags || ''} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1" placeholder="first-time, VA loan" /></div>
              <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Phone</Label><PhoneInput value={form.phone} onChange={(d) => setForm({ ...form, phone: d })} className="mt-1" /></div>
              <div className="md:col-span-2"><Label>Email</Label><Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Loan Type</Label>
                <Select value={form.loan_type} onValueChange={(v) => setForm({ ...form, loan_type: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {optionsFrom(LOAN_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(MORTGAGE_STAGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Owner</Label>
                <OwnerSelect value={form.owner_id || ''} staff={staff} onChange={(v) => setForm({ ...form, owner_id: v })} className="mt-1" testId="lead-owner-picker" />
              </div>
              <div><Label>Loan Amount</Label><MoneyInput value={form.loan_amount} onChange={(n) => setForm({ ...form, loan_amount: n })} className="mt-1" data-testid="lead-loan-amount" /></div>
              <div><Label>Credit Score</Label><NumberInput integer min={300} max={850} value={form.credit_score} onChange={(n) => setForm({ ...form, credit_score: n })} className="mt-1" data-testid="lead-credit-score" /></div>
              <AddressFields value={form.property_address_parts} onChange={(a) => setForm({ ...form, property_address_parts: a })} testIdPrefix="lead-property-address" />
              <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => setOpen(false)} className="btn-outline">Cancel</button>
            <button onClick={save} className="btn-gold" data-testid="lead-save">{editing ? 'Save' : 'Create'}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Floating bulk action bar — appears when 1+ rows selected */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white rounded-full shadow-2xl border border-slate-200 pl-5 pr-2 py-2 flex items-center gap-3" data-testid="bulk-action-bar">
          <div className="text-[13px] font-semibold" style={{ color: '#070B5F' }}>
            {selectedIds.size} selected
          </div>
          <div className="h-6 w-px bg-slate-200" />
          <Select value={bulkStage} onValueChange={setBulkStage}>
            <SelectTrigger className="h-9 w-40 text-[12px]" data-testid="bulk-stage-picker">
              <SelectValue placeholder={isRE ? 'Change type…' : 'Change status…'} />
            </SelectTrigger>
            <SelectContent>
              {(isRE ? Object.entries(RE_TYPE_LABELS) : Object.entries(MORTGAGE_STAGE_LABELS)).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!isRE && (
            <OwnerSelect value={bulkOwner} staff={staff} onChange={setBulkOwner} placeholder="Assign to…" className="h-9 w-40 text-[12px]" testId="bulk-owner-picker" />
          )}
          <button
            onClick={bulkApplyStage}
            disabled={!bulkStage && !bulkOwner}
            data-testid="bulk-apply"
            className="text-[12px] font-semibold px-3 py-1.5 rounded-full text-white disabled:opacity-50"
            style={{ background: '#070B5F' }}
          >Apply</button>
          <button
            onClick={bulkDelete}
            data-testid="bulk-delete"
            className="text-[12px] font-semibold px-3 py-1.5 rounded-full text-white"
            style={{ background: '#dc2626' }}
          >Delete</button>
          <button
            onClick={clearSelection}
            data-testid="bulk-clear"
            className="text-[12px] font-semibold px-3 py-1.5 rounded-full text-slate-500 hover:bg-slate-100"
          >Cancel</button>
        </div>
      )}
    </div>
  );
}
